import { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import firebase from '../../../firebase/compat'
import { db } from '../../../firebase/config'
import { client } from '../../../hooks/Client'
import { Auth } from '../../../StateManagment/Auth'
import { useFirestore, useFirestoreGeneral } from '../../../firebase/useFirestore'
import { publishImpactPages } from '../../../helpers/impactInBeeld/publishImpactPages'
import StatusPill from '../StatusPill.jsx'
import { deriveChannelStatus } from '../../../helpers/impactInBeeld/channelStatus'
import PrintChannelSection from './PrintChannelSection.jsx'
import LiveChannelSection from './LiveChannelSection.jsx'
import DocumentChannelSection from './DocumentChannelSection.jsx'

/**
 * Configscherm-shell voor één kanaal (fase 2 T5). De kanaal-specifieke
 * secties (print-preview, kopieer-URL, snippet) haken hier in T6/T7 in;
 * de publiceer-actie is voor alle kanaaltypen gelijk: snapshot bevriezen
 * via publishImpactPages en het kanaal op 'live' zetten.
 * See docs/features/impact-in-beeld-fase2.md.
 *
 * @param {object} props
 * @param {{id: string, title: string, description: string, outputType: string, requirements: Array<string>}} props.channel - Kanaal-config-entry
 * @param {{docid: string, Status: string, Config?: object}} props.channelDoc - Het ImpactChannels-doc van dit kanaal
 * @param {ReturnType<typeof import('../../../hooks/useImpactPerEuroRatio').default>} props.impactPerEuro - Kengetal-resultaat, éénmalig opgehaald door de hub
 */
const ChannelConfigScreen = ({ channel, channelDoc, impactPerEuro }) => {
    const history = useHistory()
    const [auth] = useContext(Auth)

    const compagny = useFirestore('CompagnyMeta')
    const allProducts = useFirestoreGeneral('Products', 'CompagnyID', client)
    const existingPublications = useFirestoreGeneral('ImpactPublications', 'CompagnyID', client)
    const { ratio, result, label, totalValue, error: ratioError, errorMessage: ratioErrorMessage } = impactPerEuro

    const [publishState, setPublishState] = useState(/** @type {'idle'|'publishing'|'published'} */ ('idle'))
    const [blockedReden, setBlockedReden] = useState('')
    // null = alle producten (default); anders expliciete selectie uit het doc.
    const [productIds, setProductIds] = useState(
        /** @type {Array<string> | null} */ (channelDoc.Config?.ProductIds || null))

    const meta = (compagny && compagny[0]) || {}
    const branding = {
        Logo: meta.Logo || '',
        Name: meta.CommunityName || '',
        PrimaryColor: meta.PrimaryColor || '',
        SecundairyColor: meta.SecundairyColor || '',
        TertairyColor: meta.TertairyColor || '',
    }

    const needsProducts = channel.requirements.includes('producten')
    const selectedProducts = needsProducts
        ? (allProducts || []).filter((product) => !productIds || productIds.includes(product.ID))
        : []

    const toggleProduct = (productId) => {
        const currentIds = productIds || (allProducts || []).map((product) => product.ID)
        setProductIds(currentIds.includes(productId)
            ? currentIds.filter((id) => id !== productId)
            : [...currentIds, productId])
    }

    const publishProducts = selectedProducts
    const config = needsProducts
        ? { ...channelDoc.Config, ProductIds: selectedProducts.map((product) => product.ID) }
        : { ...channelDoc.Config }

    const publish = async () => {
        setBlockedReden('')
        setPublishState('publishing')

        try {
            const outcome = await publishImpactPages({
                db,
                client,
                userId: auth?.ID || '',
                publishedAt: firebase.firestore.FieldValue.serverTimestamp(),
                snapshotBase: { result, label, totalValue, branding },
                products: publishProducts,
                existingPublications: existingPublications || [],
                channel: { docid: channelDoc.docid, config },
            })

            if (outcome.blocked) {
                setBlockedReden(outcome.reden)
                setPublishState('idle')
                return
            }

            setPublishState('published')
        } catch (error) {
            setPublishState('idle')
            alert(error)
        }
    }

    return (
        <div className='impact-in-beeld-tab'>
            <button
                type='button'
                className='impact-channel-config-back'
                onClick={() => history.push(`/${client}/impactinbeeld/kanalen`)}
            >
                ← Terug naar kanalen
            </button>
            <div className='impact-channel-card-header'>
                <h2>{channel.title}</h2>
                <StatusPill status={deriveChannelStatus(channelDoc)} />
            </div>
            <p className='impact-in-beeld-tab-intro'>{channel.description}</p>

            {channel.outputType === 'print' && (
                <PrintChannelSection
                    channel={channel}
                    client={client}
                    branding={branding}
                    ratio={ratio}
                    allProducts={allProducts || []}
                    selectedProducts={selectedProducts}
                    onToggleProduct={toggleProduct}
                />
            )}
            {channel.outputType === 'live' && (
                <LiveChannelSection
                    channel={channel}
                    client={client}
                    status={deriveChannelStatus(channelDoc)}
                />
            )}
            {channel.outputType === 'document' && (
                <DocumentChannelSection
                    client={client}
                    orgName={branding.Name}
                    ratio={ratio}
                />
            )}

            {ratioError && (
                <p className='impact-in-beeld-form-error'>{ratioErrorMessage}</p>
            )}
            {blockedReden && (
                <p className='impact-in-beeld-form-error'>{blockedReden}</p>
            )}
            {publishState === 'published' && (
                <p className='impact-in-beeld-tab-notice'>Gepubliceerd — de uiting toont nu de actuele cijfers als snapshot.</p>
            )}

            <button
                className='impact-in-beeld-add-button'
                onClick={publish}
                disabled={publishState === 'publishing'}
            >
                Publiceren
            </button>
        </div>
    )
}

export default ChannelConfigScreen
